/**
 * The site's other pages (About, Rules, Privacy, the live stats, a player's and your profile page) in the language the
 * menu picked (1.04.4, the owner: "do english, spanish, portuguese, and french and chinese only ... the chinese directs
 * to cn.trenchio.gg and everything in chinese"): the menu's saved settings say which (a ?language= in the address
 * first); with none, the site's own, Chinese on the Chinese site (cn.<domain>) and English everywhere else.
 *
 * What a page has in the language: each element marked `t` / `t-attr` gets its phrase's words (phrases.ts), each one
 * with `translation="key"` the game's own words for that key (`use-html`: as markup), and a page with whole copies of
 * its content in each language (`<template data-language="zh" data-for="content">`, beside its English
 * `id="content"`) shows the one for its language. English is the page as written.
 */
import { SETTINGS_STORAGE_KEY } from "./console/settingsKey";
import { onChineseSite } from "./utils/chineseSite";
import { applyLanguageLook, FULL_STOPS } from "./utils/translations/languageLook";
import { setPhrasebook, translatePhrases } from "./utils/translations/phrases";

/** the languages the site has (1.04.4), and each one's language tag */
export const PAGE_LANGUAGES: Readonly<Record<string, string>> = {
    en: "en", es: "es", br: "pt-BR", fr: "fr", de: "de", ru: "ru", ar: "ar", hi: "hi", zh: "zh-CN"
};

/** the language this page is in: the address's ?language=, else the menu's, else the site's */
export function pageLanguage(): string {
    const asked = new URLSearchParams(location.search).get("language");
    const known = (language: unknown): language is string =>
        typeof language === "string" && Object.hasOwn(PAGE_LANGUAGES, language);
    if (known(asked)) return asked;
    try {
        const saved = (JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) ?? "null") as { variables?: Record<string, unknown> } | null)?.variables?.cv_language;
        const value = typeof saved === "object" && saved !== null ? (saved as { value?: unknown }).value : saved;
        if (known(value)) return value;
    } catch {
        /* (the browser refuses this site's storage, or the settings can't be read: the site's own language) */
    }
    return onChineseSite(location.hostname) ? "zh" : "en";
}

/** puts the page in its language (see above); resolves once it is, with the language */
export async function translatePage(): Promise<string> {
    const language = pageLanguage();
    document.documentElement.lang = PAGE_LANGUAGES[language] ?? language;
    // (a page of text turned round whole for Arabic; Arabic's and Hindi's letters unspaced: languageLook.ts, 1.04.5)
    applyLanguageLook(language, true);
    if (language === "en") return language;
    const { importTranslation } = await import("virtual:translations-manifest") as {
        importTranslation: (language: string) => Promise<Record<string, unknown> & { phrases?: Record<string, string>, no_space?: boolean }>
    };
    const words = await importTranslation(language);
    setPhrasebook(words.phrases, { noSpace: words.no_space === true, fullStop: FULL_STOPS[language] });
    document.querySelectorAll<HTMLTemplateElement>(`template[data-language="${language}"][data-for]`).forEach(template => {
        const target = document.getElementById(template.dataset.for ?? "");
        if (target) target.replaceChildren(template.content.cloneNode(true));
    });
    translatePhrases(document);
    document.querySelectorAll<HTMLElement>("[translation]").forEach(element => {
        const key = element.getAttribute("translation") ?? "";
        const text = words[key];
        if (typeof text !== "string") return;
        if (element.hasAttribute("use-html")) element.innerHTML = text;
        else element.textContent = text;
    });
    return language;
}
